import type { ReactNode } from 'react';
import { Button } from './Button';
import { cn } from './cn';

/**
 * Empty, loading and error states.
 *
 * These exist as primitives because "no blank screens" is in the definition of
 * done, and a blank screen is what you get when every table invents its own
 * empty case and one of them forgets.
 */

/**
 * An empty state that says what would fill it.
 *
 * Never the words "no data". A person reading that cannot tell whether the
 * query is wrong, the filter is too narrow, or the work genuinely has not
 * happened yet — and those need three different next actions.
 */
export function EmptyState({ title, hint, action, icon }: {
  title: string;
  hint?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className={cn(
      'flex flex-col items-center justify-center rounded-card border border-dashed border-neutral-300 bg-neutral-50/50 px-6 py-12 text-center',
      // Inside a card the card is the frame: a second, dashed one drawn
      // flush against the card's edge and its heading read as broken.
      '[.shadow-card_&]:border-transparent [.shadow-card_&]:bg-transparent',
    )}>
      {icon && (
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100 text-neutral-400" aria-hidden>
          {icon}
        </div>
      )}
      <p className="text-body font-semibold text-neutral-800">{title}</p>
      {hint && <p className="mt-1 max-w-sm text-small text-neutral-500">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/**
 * An error state that says what to do next.
 *
 * `detail` comes from the API's problem document, which is written for a
 * person — "Assign a job role with mapped hazards before raising an
 * examination" is actionable in a way "422 Unprocessable Entity" is not.
 */
export function ErrorState({ title = 'Something went wrong', detail, onRetry }: {
  title?: string;
  detail?: string;
  onRetry?: () => void;
}) {
  return (
    <div role="alert" className="rounded-card border border-danger-600/25 bg-danger-100 px-4 py-5 text-center">
      <p className="text-body font-semibold text-danger-600">{title}</p>
      {detail && <p className="mt-1 text-small text-neutral-700">{detail}</p>}
      {onRetry && (
        <div className="mt-3">
          <Button variant="secondary" size="sm" onClick={onRetry}>Try again</Button>
        </div>
      )}
    </div>
  );
}

/**
 * An inline error banner, for a failure the page can carry on around.
 *
 * Distinct from ErrorState, which replaces the content it failed to load.
 */
export function ErrorBanner({ message, className }: { message: string; className?: string }) {
  return (
    <div
      role="alert"
      className={cn('rounded-card border border-danger-600/25 bg-danger-100 px-3.5 py-2.5 text-body text-danger-600', className)}
    >
      {message}
    </div>
  );
}

/**
 * A short inline message inside a dialog or a form.
 *
 * Distinct from ErrorBanner only in weight: this one sits under the control
 * that caused it and is sized not to shout.
 */
export function Alert({ tone = 'danger', title, children, className }: {
  tone?: 'danger' | 'warning' | 'info' | 'success';
  /**
   * An optional heading.
   *
   * Worth having because the longer alerts on this platform explain a rule
   * rather than report a failure — "history is preserved", "hazards come from
   * the job role" — and a reader skimming a form needs to know whether the
   * paragraph is about them before reading it.
   */
  title?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const TONE = {
    danger: 'border-danger-600/25 bg-danger-100 text-danger-600',
    warning: 'border-warning-600/25 bg-warning-100 text-warning-600',
    info: 'border-info-600/25 bg-info-100 text-info-600',
    success: 'border-success-600/25 bg-success-100 text-success-600',
  } as const;
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('rounded-input border px-3 py-2 text-small', TONE[tone], className)}
    >
      {title && <p className="font-semibold">{title}</p>}
      {children}
    </div>
  );
}

/**
 * Skeletons, shaped like the content they replace.
 *
 * A spinner tells the reader to wait; a skeleton tells them what is coming and
 * stops the layout jumping when it arrives.
 */
export function TableSkeleton({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="animate-pulse" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-4 border-b border-neutral-100 px-4" style={{ height: 56 }}>
          {Array.from({ length: cols }).map((_, c) => (
            <div
              key={c}
              className="h-3 rounded bg-neutral-100"
              style={{ width: c === 0 ? '18%' : `${12 + ((r + c) % 3) * 6}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function StatSkeleton({ count = 4 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="animate-pulse rounded-card border border-neutral-200 bg-white p-5" aria-hidden>
          <div className="h-3 w-24 rounded bg-neutral-100" />
          <div className="mt-3 h-8 w-16 rounded bg-neutral-100" />
          <div className="mt-2 h-2.5 w-20 rounded bg-neutral-100" />
        </div>
      ))}
    </>
  );
}

export function TextSkeleton({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('animate-pulse space-y-2', className)} aria-busy="true">
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="h-3 rounded bg-neutral-100"
          style={{ width: i === lines - 1 ? '60%' : '100%' }}
        />
      ))}
    </div>
  );
}

/**
 * "Load more" for a list that is not a DataTable — a queue of cards, a list
 * of tubes. The same control DataTable renders under its rows, so a paged
 * list looks the same wherever it is. Says how many are shown, because a
 * count on a tile above is only a count of what has been loaded.
 */
export function LoadMore({ hasMore, loading, onLoadMore, shown, className }: {
  hasMore: boolean;
  loading?: boolean;
  onLoadMore: () => void;
  /** How many rows are on screen now. */
  shown?: number;
  className?: string;
}) {
  if (!hasMore) return null;
  return (
    <div className={cn('flex flex-wrap items-center justify-center gap-2 py-2', className)}>
      {shown != null && <span className="text-caption font-normal text-neutral-500">{shown} shown</span>}
      <button
        type="button"
        onClick={onLoadMore}
        disabled={loading}
        className="rounded-btn px-3 py-1.5 text-small font-medium text-primary-600 hover:bg-primary-50 disabled:text-neutral-500"
      >
        {loading ? 'Loading…' : 'Load more'}
      </button>
    </div>
  );
}
